import { useId, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { labelClass } from './authFieldStyles';

const LENGTH = 6;

type Props = {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  onBlur?: () => void;
  error?: string;
};

// Segmented 6-box code entry, auto-advancing focus per digit and calling `onComplete` the
// instant all 6 are filled (callers wire that to auto-submit) — replaces a single free-text
// input that had neither.
export function AuthOtpField({ value, onChange, onComplete, onBlur, error }: Props) {
  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? '');
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const baseId = useId();

  function commit(next: string[]) {
    const joined = next.join('');
    onChange(joined);
    if (joined.length === LENGTH && next.every((d) => d !== '')) {
      onComplete?.(joined);
    }
  }

  function handleChange(i: number, raw: string) {
    const char = raw.replace(/\D/g, '').slice(-1);
    const next = digits.slice();
    next[i] = char;
    commit(next);
    if (char && i < LENGTH - 1) inputsRef.current[i + 1]?.focus();
  }

  function handleKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      inputsRef.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < LENGTH - 1) {
      e.preventDefault();
      inputsRef.current[i + 1]?.focus();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array.from({ length: LENGTH }, (_, i) => pasted[i] ?? '');
    commit(next);
    const lastFilled = Math.min(pasted.length, LENGTH - 1);
    inputsRef.current[lastFilled]?.focus();
  }

  return (
    <div>
      <label htmlFor={`${baseId}-0`} className={labelClass}>Verification code</label>
      <div className="flex gap-2">
        {digits.map((d, i) => (
          <input
            key={i}
            id={i === 0 ? `${baseId}-0` : undefined}
            ref={(el) => { inputsRef.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onBlur={i === LENGTH - 1 ? onBlur : undefined}
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${i + 1} of ${LENGTH}`}
            className="w-full aspect-square min-w-0 text-center text-xl sm:text-2xl font-black bg-[#1c1c1e] border-2 border-[#424245] text-white rounded-2xl outline-none focus:border-accent transition-colors"
          />
        ))}
      </div>
      {error && <p className="text-red-400 text-xs mt-1.5">{error}</p>}
    </div>
  );
}
